import { Box, Slider } from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import { useTranslation } from 'react-i18next';
import { useStudio, type StudioItem } from '../../context';
import {
  StudioSideBySide,
  StudioSideBySideContainer,
  StudioSidebarInterior,
  StudioSidebarTitle,
} from '../lib';

export interface StudioSettingsFormatTabShapesProps {
  selectedItem: Extract<StudioItem, { type: 'circle' | 'rect' | 'triangle' }>;
}

export const StudioSettingsFormatTabShapes = ({
  selectedItem,
}: StudioSettingsFormatTabShapesProps) => {
  const disabled = selectedItem.locked;
  const { t } = useTranslation();
  const { updateItems } = useStudio();

  // Past half the shorter side the device renderer draws elliptical corners and stops matching.
  const maxCornerRadius = Math.floor(
    Math.min(selectedItem.width ?? 0, selectedItem.height ?? 0) / 2,
  );

  // Stored in pixels for both renderers, shown as 0-100 so the slider reads the same on any shape.
  const cornerRadius =
    typeof selectedItem.cornerRadius === 'number' ? selectedItem.cornerRadius : 0;
  const rounding = maxCornerRadius
    ? Math.min(100, Math.round((cornerRadius / maxCornerRadius) * 100))
    : 0;

  return (
    <StudioSidebarInterior>
      <StudioSidebarTitle>{t('pages.studio.edit.settings.shapes.fill')}</StudioSidebarTitle>

      <MuiColorInput
        disabled={disabled}
        format="hex"
        isAlphaHidden={true}
        onChange={(fill) => updateItems([{ id: selectedItem.id, fill }])}
        value={
          // eslint-disable-next-line no-restricted-syntax -- fallback for a user-picked color, not a theme color
          typeof selectedItem.fill === 'string' ? selectedItem.fill : '#000000'
        }
      />

      <StudioSidebarTitle>{t('pages.studio.edit.settings.shapes.border')}</StudioSidebarTitle>

      <Box>
        <StudioSideBySideContainer>
          <StudioSideBySide>
            <MuiColorInput
              disabled={disabled}
              format="hex"
              isAlphaHidden={true}
              onChange={(stroke) => updateItems([{ id: selectedItem.id, stroke }])}
              value={
                // eslint-disable-next-line no-restricted-syntax -- fallback for a user-picked color, not a theme color
                typeof selectedItem.stroke === 'string' ? selectedItem.stroke : '#000000'
              }
            />
          </StudioSideBySide>
          <StudioSideBySide>
            <Slider
              disabled={disabled}
              min={0}
              max={50}
              onChange={(_, strokeWidth) => updateItems([{ id: selectedItem.id, strokeWidth }])}
              step={1}
              value={selectedItem.strokeWidth || 0}
              valueLabelDisplay="auto"
            />
          </StudioSideBySide>
        </StudioSideBySideContainer>
      </Box>

      {selectedItem.type === 'rect' && (
        <StudioSideBySideContainer>
          <StudioSideBySide>
            <StudioSidebarTitle>
              {t('pages.studio.edit.settings.shapes.corner_rounding')}
            </StudioSidebarTitle>
          </StudioSideBySide>

          <StudioSideBySide>
            <Slider
              disabled={disabled}
              min={0}
              max={100}
              onChange={(_, value) =>
                updateItems([
                  {
                    id: selectedItem.id,
                    cornerRadius: Math.round((Number(value) / 100) * maxCornerRadius),
                  },
                ])
              }
              step={1}
              value={rounding}
              valueLabelDisplay="auto"
            />
          </StudioSideBySide>
        </StudioSideBySideContainer>
      )}
    </StudioSidebarInterior>
  );
};
